

export const dynamic = 'force-dynamic';

import { useState } from "react";
import VendorReportsLayout, { useVendorPeriod, type Period } from "../VendorReportsLayout";
import { reportsApi } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import {
  Card, ExportButtons, Kpi, KpiGrid, NoMoneyAccess, Note, ReportBody, TableScroll, money, th, td, useCanSeeMoney, useReport,
  type ExportSpec,
} from "../../ReportKit";

interface Commission {
  id: number;
  vendor_name: string;
  vendor_type: string | null;
  event_name: string;
  client_name: string | null;
  event_date: string | null;
  event_status: string | null;
  description: string | null;
  commission_type: string;
  commission_percent: number;
  commission_amount: number;
  charged: number;
  cost: number;
}

interface CommissionsReport {
  summary: {
    total_commission: number;
    total_assignments: number;
    average_commission: number;
    by_vendor: { id: number; name: string; total_commission: number; assignment_count: number }[];
    by_commission_type: { commission_type: string; total_commission: number; count: number }[];
  };
  commissions: { data: Commission[]; current_page: number; last_page: number; total: number };
}

/** The server's largest page; the export walks pages up to EXPORT_MAX_ROWS rows. */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 1000;

/** Excel / Print: the KPIs, commission per vendor and every commission line in the period (not just the page on screen). */
async function buildExport(period: Period): Promise<ExportSpec> {
  const fetchPage = async (page: number): Promise<CommissionsReport> =>
    (await reportsApi.vendorCommissions({ from_date: period.from, to_date: period.to, page, per_page: EXPORT_PAGE_SIZE })).data;
  const first = await fetchPage(1);
  const rows: Commission[] = [...first.commissions.data];
  const pages = Math.min(first.commissions.last_page, EXPORT_MAX_ROWS / EXPORT_PAGE_SIZE);
  for (let page = 2; page <= pages; page++) rows.push(...(await fetchPage(page)).commissions.data);

  const s = first.summary;
  const total = first.commissions.total;
  const complete = rows.length >= total;
  return {
    title: "Vendor Commissions",
    fileName: "vendor-commissions",
    period,
    summary: [
      { label: "Total commission", value: s.total_commission, type: "money" },
      { label: "Lines with commission", value: s.total_assignments, type: "number" },
      { label: "Average per line", value: s.average_commission, type: "money" },
      { label: "Vendors", value: s.by_vendor.length, type: "number" },
    ],
    tables: [
      {
        title: "Commission by vendor",
        columns: [{ label: "Vendor" }, { label: "Lines", type: "number" }, { label: "Commission", type: "money" }],
        rows: s.by_vendor.map((v) => [v.name, v.assignment_count, v.total_commission]),
        totals: ["Total", s.total_assignments, s.total_commission],
      },
      {
        title: "Commission details",
        columns: [
          { label: "Vendor" }, { label: "Category" }, { label: "Event" }, { label: "Client" }, { label: "Date", type: "date" },
          { label: "Charged", type: "money" }, { label: "Rate" }, { label: "Commission", type: "money" },
        ],
        rows: rows.map((c) => [
          c.vendor_name, c.vendor_type, c.event_name, c.client_name, c.event_date,
          c.charged, c.commission_type === "percentage" ? `${c.commission_percent}%` : "Fixed", c.commission_amount,
        ]),
        totals: complete ? ["Total", null, null, null, null, null, null, s.total_commission] : undefined,
      },
    ],
    notes: [
      "Cancelled events earn no commission.",
      ...(complete ? [] : [`Commission details show the first ${rows.length} of ${total} lines; the totals above cover all of them.`]),
    ],
  };
}

export default function VendorCommissionsPage() {
  const [period, setPeriod] = useVendorPeriod();
  const canSeeMoney = useCanSeeMoney();
  const [page, setPage] = useState(1);

  const state = useReport<CommissionsReport>(
    async () => (await reportsApi.vendorCommissions({ from_date: period.from, to_date: period.to, page, per_page: 20 })).data,
    [period.from, period.to, page],
    canSeeMoney,
  );

  return (
    <VendorReportsLayout
      period={period}
      onPeriodChange={(p) => { setPeriod(p); setPage(1); }}
      actions={canSeeMoney && (
        <ExportButtons
          build={() => buildExport(period)}
          disabled={state.loading || !state.data?.commissions.total}
        />
      )}
    >
      {!canSeeMoney ? <NoMoneyAccess what="commission figures" /> : (
        <ReportBody state={state}>
          {(d) => {
            const s = d.summary;
            const { data: rows, current_page, last_page } = d.commissions;
            return (
              <>
                <KpiGrid>
                  <Kpi label="Total commission" value={money(s.total_commission)} tone="violet" />
                  <Kpi label="Lines with commission" value={s.total_assignments} />
                  <Kpi label="Average per line" value={money(s.average_commission)} tone="amber" />
                  <Kpi label="Vendors" value={s.by_vendor.length} tone="emerald" />
                </KpiGrid>

                {s.by_vendor.length > 0 && (
                  <Card title="Commission by vendor">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {s.by_vendor.slice(0, 6).map((v) => (
                        <div key={v.id} className="bg-stone-50 rounded-xl p-3 border border-stone-200 min-w-0">
                          <div className="font-semibold text-stone-800 text-sm truncate">{v.name}</div>
                          <div className="text-lg font-bold text-violet-700 tabular-nums">{money(v.total_commission)}</div>
                          <div className="text-xs text-stone-400">{v.assignment_count} line{v.assignment_count === 1 ? "" : "s"}</div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                <Card title="Commission details">
                  {rows.length === 0 ? (
                    <p className="py-8 text-center text-sm text-stone-400">No commission on events in this period.</p>
                  ) : (
                    <>
                      <TableScroll minWidth={760}>
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-100">
                              <th className={`${th} text-left`}>Vendor</th>
                              <th className={`${th} text-left`}>Event</th>
                              <th className={`${th} text-left`}>Date</th>
                              <th className={`${th} text-right`}>Charged</th>
                              <th className={`${th} text-center`}>Rate</th>
                              <th className={`${th} text-right`}>Commission</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-stone-50">
                            {rows.map((c) => (
                              <tr key={c.id} className="hover:bg-stone-50/50">
                                <td className={td}>
                                  <div className="font-semibold text-stone-800">{c.vendor_name}</div>
                                  {c.vendor_type && <div className="text-xs text-stone-400">{c.vendor_type}</div>}
                                </td>
                                <td className={td}>
                                  <div className="font-medium text-stone-700">{c.event_name}</div>
                                  {c.client_name && <div className="text-xs text-stone-400">{c.client_name}</div>}
                                </td>
                                <td className={`${td} text-xs text-stone-500`}>{c.event_date ? formatDate(c.event_date) : "—"}</td>
                                <td className={`${td} text-right tabular-nums text-stone-600`}>{money(c.charged)}</td>
                                <td className={`${td} text-center text-stone-600`}>{c.commission_type === "percentage" ? `${c.commission_percent}%` : "Fixed"}</td>
                                <td className={`${td} text-right tabular-nums font-bold text-violet-700`}>{money(c.commission_amount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </TableScroll>
                      {last_page > 1 && (
                        <div className="flex items-center justify-between pt-3 mt-3 border-t border-stone-100">
                          <button onClick={() => setPage(current_page - 1)} disabled={current_page <= 1}
                            className="h-10 px-3 text-sm rounded-lg border border-stone-200 disabled:opacity-40">← Previous</button>
                          <span className="text-xs text-stone-500">Page {current_page} of {last_page}</span>
                          <button onClick={() => setPage(current_page + 1)} disabled={current_page >= last_page}
                            className="h-10 px-3 text-sm rounded-lg border border-stone-200 disabled:opacity-40">Next →</button>
                        </div>
                      )}
                    </>
                  )}
                </Card>
                <Note>Totals cover every commission line in the period, not just this page. Cancelled events earn no commission.</Note>
              </>
            );
          }}
        </ReportBody>
      )}
    </VendorReportsLayout>
  );
}
